import styled, { useTheme } from 'styled-components'
import ContentLoader from 'react-content-loader'
import { useCallback, useState } from 'react'
import { USDC, findVaultNameById } from '@kunalabs-io/kai'

import { SavActivityHistory } from 'modules/ActivityHistory/SavActivityHistory'
import { DepositWithdrawCard } from './DepositWithdrawCard'
import { useUserVaultInfo } from 'utils/useUserVaultInfo'
import { useVaultInfo } from '@/shared/kai/hooks/useVaultInfo'
import { frozenVaults, VaultDisplayInfo, vaultDisplayInfoList } from 'utils/vaultDisplayInfo'
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs'
import { VaultsMenu } from './VaultsMenu'
import { SingleVaultExplanation } from './SingleVaultExplanation'
import { Overview } from './Overview'
import { Allocation } from './Allocation'
import { useSAVActivityHistory } from './hooks/useSAVActivityHistory'
import { ApyTvlCharts } from './Charts/ApyTvlCharts'
import { useSAVMetrics } from './hooks/useSAVMetrics'
import { Banner } from 'components/Banner/Banner'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { useTokenPrice } from 'utils/useTokenPrice'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { usePostHogEntityViewed } from 'utils/usePostHogEntityViewed'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'
import { useGlobalBanner } from 'utils/useGlobalBanner'
import { GLOBAL_BANNER_HEIGHT } from 'components/GlobalBanner/GlobalBanner'

export const DONT_SHOW_THIS_AGAIN_LS_KEY = 'single-asset-vault-not-show'

const Page = styled.div<{ $hasBanner: boolean }>`
  min-height: calc(100vh - 462px);
  margin-top: calc(124px + ${props => (props.$hasBanner ? GLOBAL_BANNER_HEIGHT : 0)}px);

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 0 20px;
    margin-top: calc(100px + ${props => (props.$hasBanner ? GLOBAL_BANNER_HEIGHT : 0)}px);
  }

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
  }
`

const DetailsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  margin: auto;
  margin-top: 16px;
  align-items: stretch;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    margin-top: 0;
    // padding: 0 20px;
  }
`

const LeftContainer = styled.div`
  width: 60%;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 100%;
  }
`
const DetailsCardContainerItem = styled.div`
  width: 40%;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 100%;
  }
`

/*
const Address = styled(Typography)`
  color: ${p => p.theme.colors.text.description};
  margin-right: 10px;
`

const AddressInfo = styled.div`
  display: flex;
  align-items: center;
  svg {
    margin-right: 10px;
  }
`
*/

export const DetailsContentLoading = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={150}
      height={25}
      viewBox="0 0 150 25"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="8" rx="3" ry="3" width="75" height="7" />
      <rect x="0" y="26" rx="3" ry="3" width="150" height="7" />
    </ContentLoader>
  )
}

export interface VaultDetailsProps {
  displayInfo: VaultDisplayInfo
}

const getDisplayTooltipValue = (value: number, decimals: number, symbol: string) =>
  `${value.toLocaleString('en-US', { maximumFractionDigits: decimals })} ${symbol}`

export const VaultDetails = ({ displayInfo }: VaultDetailsProps) => {
  const { isEnabled: hasBanner } = useGlobalBanner()

  usePostHogEntityViewed('sav:vault_details_view', {
    entityId: displayInfo.vault.id,
    entityName: findVaultNameById(displayInfo.vault.id),
  })
  const userInfo = useUserVaultInfo(displayInfo.vault)
  const vaultInfo = useVaultInfo(displayInfo.vault, { refetchInterval: 5000 })
  const walletAddress = useCurrentWalletAddress()
  const { data: savMetrics, isLoading: isLoadingSavMetrics } = useSAVMetrics()
  const { price } = useTokenPrice(displayInfo.vault.T, USDC)

  const { data, isLoading, refetch: refetchSavActivityHistory } = useSAVActivityHistory(displayInfo.vault.id)

  const [isDontShowSelected, setIsDontShowSelected] = useState(
    localStorage.getItem(DONT_SHOW_THIS_AGAIN_LS_KEY) === 'true'
  )

  const setDontShowThisAgain = useCallback(() => {
    setIsDontShowSelected(true)
    localStorage.setItem(DONT_SHOW_THIS_AGAIN_LS_KEY, 'true')
  }, [])

  const resetDontShowThisAgain = useCallback(() => {
    setIsDontShowSelected(false)
    localStorage.setItem(DONT_SHOW_THIS_AGAIN_LS_KEY, 'false')
  }, [])

  const currentSAVMetric = savMetrics?.find(s => s.vaultId === displayInfo.vault.id)

  const decimals = displayInfo.vault.T.decimals
  const symbol = displayInfo.vault.T.displaySymbol

  const equity = userInfo.equity && displayInfo.formatAmount(userInfo.equity)
  const formattedEquity = userInfo.equity && getDisplayTooltipValue(userInfo.equity.toNumber(), decimals, symbol)
  const deposited =
    userInfo.inventoryAtCost && getDisplayTooltipValue(userInfo.inventoryAtCost.toNumber(), decimals, symbol)
  const formattedRealizedProfit =
    userInfo.realizedProfit && getDisplayTooltipValue(userInfo.realizedProfit.toNumber(), decimals, symbol)
  const formattedUnRealizedProfit =
    userInfo.unrealizedProfit && getDisplayTooltipValue(userInfo.unrealizedProfit.toNumber(), decimals, symbol)
  const earnedNum =
    userInfo.realizedProfit && userInfo.unrealizedProfit
      ? userInfo.realizedProfit.toNumber() + userInfo.unrealizedProfit.toNumber()
      : undefined

  const formattedEarned = earnedNum ? getDisplayTooltipValue(earnedNum, decimals, symbol) : undefined

  const earnedNumUsd = price && typeof earnedNum === 'number' ? earnedNum * price.human.toNumber() : undefined

  const earned =
    userInfo.realizedProfit &&
    userInfo.unrealizedProfit &&
    displayInfo.formatAmount(userInfo.realizedProfit.toNumber() + userInfo.unrealizedProfit.toNumber())
  const earnedUsd = typeof earnedNumUsd === 'number' ? getFormattedUsdValue(earnedNumUsd) : undefined
  const equityUsd =
    userInfo.equity && price ? getFormattedUsdValue(userInfo.equity.toNumber() * price.human.toNumber()) : undefined

  const tvl = vaultInfo.tvl && displayInfo.formatAmount(vaultInfo.tvl)
  const tvlUsd =
    vaultInfo.tvl && price ? getFormattedUsdValue(vaultInfo.tvl.toNumber() * price.human.toNumber()) : undefined
  const performanceFee = vaultInfo.performanceFee !== undefined ? vaultInfo.performanceFee * 100 + '%' : ''

  const maxWithdrawable = vaultInfo.maxWithdrawable && displayInfo.formatAmount(vaultInfo.maxWithdrawable)
  const utilization = vaultInfo.utilization !== undefined ? (vaultInfo.utilization * 100).toFixed(2) + '%' : ''

  // const isTvlCapReached = vaultInfo.tvlCap && vaultInfo.tvl && vaultInfo.tvl.int >= vaultInfo.tvlCap.int

  /*
  const handleAddressClick = useCallback(async () => {
    console.log('handleAddressClick')
    await navigator.clipboard.writeText(account?.address || '')
    toast.success('Address copied')
  }, [account?.address])
  */

  const showDepositedLoader = !userInfo.inventoryAtCost || !userInfo.equity

  const isFrozenVault = frozenVaults.some(fv => fv.vault.id === displayInfo.vault.id)

  const isMobile = useIsMobile()

  return (
    <Page $hasBanner={hasBanner}>
      <Breadcrumbs
        items={[
          { label: 'Home', path: '/' },
          { label: 'Vaults', path: '/vaults' },
          { label: displayInfo.tSymbol, path: `/vaults/${displayInfo.tSymbol}` },
        ]}
      />
      <VaultsMenu
        displayInfo={displayInfo}
        isDontShowSelected={isDontShowSelected}
        onResetDontShowThisAgain={resetDontShowThisAgain}
        displayInfoList={[...vaultDisplayInfoList, ...frozenVaults]}
      />
      <SingleVaultExplanation isDontShowSelected={isDontShowSelected} onDontShowThisAgainClick={setDontShowThisAgain} />
      {isFrozenVault && (
        <Banner style={isMobile ? { marginBottom: 20 } : { marginTop: 12 }}>
          <Typography variant={isMobile ? 'caption2' : 'body'} fontWeight="semiBold">
            This vault is in withdraw-only mode due to the Cetus incident. Stay informed by following updates on{' '}
            <a href="https://discord.gg/XV2xYZjReX" target="_blank" rel="noreferrer">
              Kai discord
            </a>
            .
          </Typography>
        </Banner>
      )}
      <DetailsContainer>
        <LeftContainer>
          <Overview
            equity={equity}
            earned={earned}
            earnedUsd={earnedUsd}
            equityUsd={equityUsd}
            realizedProfit={formattedRealizedProfit}
            unrealizedProfit={formattedUnRealizedProfit}
            tooltipFormattedEarned={formattedEarned}
            tooltipFormattedEquity={formattedEquity}
            apy={vaultInfo.apy}
            apy7d={currentSAVMetric?.apy7d}
            apy30d={currentSAVMetric?.apy30d}
            strategyYieldRatio24h={currentSAVMetric?.strategyYieldRatio24h}
            deposited={deposited}
            tvl={tvl}
            tvlUsd={tvlUsd}
            showDepositedLoader={showDepositedLoader}
            performanceFee={performanceFee}
            isLoadingApyAvg={isLoadingSavMetrics}
          />
          <Allocation utilization={utilization} maxWithdrawable={maxWithdrawable} />
        </LeftContainer>
        <DetailsCardContainerItem>
          <DepositWithdrawCard
            vaultDisplayInfo={displayInfo}
            vaultInfo={vaultInfo}
            maxWithdrawable={vaultInfo.maxWithdrawable}
            refetchSavActivityHistory={refetchSavActivityHistory}
          />
        </DetailsCardContainerItem>
      </DetailsContainer>
      <ApyTvlCharts vaultId={displayInfo.vault.id} />
      {walletAddress && (
        <SavActivityHistory
          isLoading={isLoading}
          events={data}
          vaultDisplayInfo={displayInfo}
          address={walletAddress}
        />
      )}
    </Page>
  )
}
